'use client';

import type { PostHeroProps } from '@routeroyal/theme-core';
import { useState } from 'react';
import Image from 'next/image';

/**
 * Route Royal Post Hero
 * 
 * Professional hero section with:
 * - Blue gradient background option
 * - Centered content
 * - Optional search
 */
export function RouteRoyalPostHero({
  title,
  subtitle,
  backgroundImage,
  showSearch = false,
  onSearch,
  searchPlaceholder = 'Search posts...',
  className = '',
}: PostHeroProps) {
  const [searchValue, setSearchValue] = useState('');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(searchValue);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch?.(value);
  };

  return (
    <section
      className={`
        relative py-20 md:py-28 lg:py-32
        ${backgroundImage ? 'text-white' : 'bg-gradient-to-br from-blue-600 to-blue-800 text-white'}
        ${className}
      `}
    >
      {backgroundImage && (
        <>
          <div className="absolute inset-0">
            <Image
              src={backgroundImage}
              alt=""
              fill
              className="object-cover"
              priority
            />
          </div>
          <div className="absolute inset-0 bg-blue-900/70" />
        </>
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          {title}
        </h1>

        {subtitle && (
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
            {subtitle}
          </p>
        )}

        {showSearch && onSearch && (
          <form onSubmit={handleSearchSubmit} className="max-w-lg mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="
                  w-full px-5 py-3.5 pr-12 rounded-lg
                  text-gray-900 placeholder-gray-500
                  bg-white shadow-xl
                  border-0 focus:ring-2 focus:ring-blue-300
                  transition-shadow hover:shadow-2xl
                "
              />
              <button
                type="submit"
                className="
                  absolute right-3 top-1/2 -translate-y-1/2
                  p-2 text-gray-500 hover:text-blue-600
                  transition-colors
                "
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
